import {
  FINANCE_ENTRY_TYPE_LABELS,
  FINANCE_RECURRENCE_LABELS,
  FINANCE_RECURRENCES,
  type FinanceEntryTypeValue,
  fromDateInput,
  toDateInput,
} from '@crm/shared';
import { Checkbox, Label, Modal, Select, Textarea } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '@/components';
import { useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  type FinanceBill,
  useCreateFinanceBill,
  useFinanceCategories,
  useUpdateFinanceBill,
} from '../api';

const FORM_ID = 'finance-bill-form';

const billFormSchema = z
  .object({
    type: z.enum(['PAYABLE', 'RECEIVABLE']),
    recurrence: z.enum(FINANCE_RECURRENCES),
    description: z.string().min(2, 'Descreva a conta').max(160),
    amount: z.coerce.number().positive('Informe um valor maior que zero'),
    startDate: z.string().min(1, 'Informe o primeiro vencimento'),
    endDate: z.string(),
    categoryId: z.string(),
    unitId: z.string(),
    notes: z.string().max(2000),
    isActive: z.boolean(),
  })
  .refine((values) => !values.endDate || values.endDate >= values.startDate, {
    message: 'O fim nao pode ser antes do primeiro vencimento',
    path: ['endDate'],
  });

type BillFormValues = z.infer<typeof billFormSchema>;

interface BillDialogProps {
  open: boolean;
  /** Preenchido = edicao; vazio = cadastro novo. */
  bill?: FinanceBill | null;
  /** Tipo ja escolhido pela tela que abriu a modal. */
  defaultType?: FinanceEntryTypeValue;
  onClose: () => void;
}

/**
 * Cadastro da conta em modal - e a mesma janela nas telas de contas a pagar, a
 * receber e no cadastro. Salvou, a lista atras se atualiza sozinha.
 */
export function BillDialog({ open, bill, defaultType = 'PAYABLE', onClose }: BillDialogProps) {
  const { user } = useSession();
  const units = user?.units ?? [];

  const createBill = useCreateFinanceBill();
  const updateBill = useUpdateFinanceBill();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<BillFormValues>({
    resolver: zodResolver(billFormSchema),
    defaultValues: {
      type: defaultType,
      recurrence: 'MONTHLY',
      description: '',
      amount: 0,
      startDate: toDateInput(new Date()),
      endDate: '',
      categoryId: '',
      unitId: '',
      notes: '',
      isActive: true,
    },
  });

  const type = watch('type');
  const recurrence = watch('recurrence');
  const categories = useFinanceCategories({ type, onlyActive: true });

  // Cada abertura comeca do estado certo: o cadastro em edicao ou um em branco.
  useEffect(() => {
    if (!open) return;

    reset({
      type: bill?.type ?? defaultType,
      recurrence: bill?.recurrence ?? 'MONTHLY',
      description: bill?.description ?? '',
      amount: bill?.amount ?? 0,
      startDate: toDateInput(bill?.startDate ?? new Date()),
      endDate: bill?.endDate ? toDateInput(bill.endDate) : '',
      categoryId: bill?.categoryId ?? '',
      unitId: bill?.unitId ?? '',
      notes: bill?.notes ?? '',
      isActive: bill?.isActive ?? true,
    });
  }, [open, bill, defaultType, reset]);

  function onSubmit(values: BillFormValues) {
    const input = {
      type: values.type,
      recurrence: values.recurrence,
      description: values.description,
      amount: values.amount,
      startDate: fromDateInput(values.startDate),
      endDate: values.endDate ? fromDateInput(values.endDate) : undefined,
      categoryId: values.categoryId || undefined,
      unitId: values.unitId || undefined,
      notes: values.notes || undefined,
      isActive: values.isActive,
    };

    if (bill) {
      updateBill.mutate({ id: bill.id, input }, { onSuccess: onClose });
      return;
    }

    createBill.mutate(input, { onSuccess: onClose });
  }

  const error = createBill.error ?? updateBill.error;
  const isSaving = createBill.isPending || updateBill.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={bill ? 'Editar conta' : 'Cadastrar conta'}
      subtitle="Do cadastro saem os vencimentos das telas de contas a pagar e a receber."
      size="medium"
      actions={[
        { label: 'Cancelar', variant: 'ghost', onClick: onClose },
        {
          label: bill ? 'Salvar' : 'Cadastrar',
          type: 'submit',
          formId: FORM_ID,
          loading: isSaving,
        },
      ]}
    >
      <form id={FORM_ID} className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
        {error ? <p className="text-sm text-destructive">{toErrorMessage(error)}</p> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="type">Tipo</Label>
            <Select id="type" {...register('type')}>
              {(['PAYABLE', 'RECEIVABLE'] as FinanceEntryTypeValue[]).map((option) => (
                <option key={option} value={option}>
                  {FINANCE_ENTRY_TYPE_LABELS[option]}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recurrence">Repeticao</Label>
            <Select id="recurrence" {...register('recurrence')}>
              {FINANCE_RECURRENCES.map((option) => (
                <option key={option} value={option}>
                  {FINANCE_RECURRENCE_LABELS[option]}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <FormField
          label="Descricao"
          placeholder="Aluguel da matriz"
          error={errors.description?.message}
          {...register('description')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Valor (R$)"
            type="number"
            step="0.01"
            min="0"
            error={errors.amount?.message}
            {...register('amount')}
          />
          <FormField
            label={recurrence === 'NONE' ? 'Vencimento' : 'Primeiro vencimento'}
            type="date"
            error={errors.startDate?.message}
            {...register('startDate')}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="categoryId">Categoria</Label>
            <Select id="categoryId" {...register('categoryId')}>
              <option value="">Sem categoria</option>
              {(categories.data?.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </div>

          {recurrence === 'NONE' ? null : (
            <FormField
              label="Ate quando (opcional)"
              type="date"
              error={errors.endDate?.message}
              {...register('endDate')}
            />
          )}
        </div>

        {units.length > 1 ? (
          <div className="space-y-2">
            <Label htmlFor="unitId">Unidade</Label>
            <Select id="unitId" {...register('unitId')}>
              <option value="">Empresa toda</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="notes">Observacoes</Label>
          <Textarea id="notes" {...register('notes')} />
        </div>

        <Checkbox label="Conta ativa (gera os proximos vencimentos)" {...register('isActive')} />
      </form>
    </Modal>
  );
}
